import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import Header from '../components/Header';
import Button from '../components/Button';
import { getPaymentStatus } from '../services/api';

const PaymentFailedContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const FailureCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const IconContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const FailureIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #f8d7da;
  color: #e74c3c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #e74c3c;
`;

const Message = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  color: #666;
`;

const ResponseCode = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-weight: 500;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #666;
`;

const DetailValue = styled.span`
  color: #333;
`;

const ActionButton = styled(Button)`
  margin-top: 1.5rem;
`;

const PaymentFailed = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState(null);
  
  // Get query parameters
  const queryParams = new URLSearchParams(location.search);
  const referenceNo = queryParams.get('reference');
  const responseCode = queryParams.get('code');
  const error = queryParams.get('error');
  const programId = queryParams.get('program_id');
  
  // Get payment status from API
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!referenceNo) {
        setLoading(false);
        setErrorType(error || 'unknown');
        return;
      }
      
      try {
        const response = await getPaymentStatus(referenceNo);
        setPaymentDetails(response);
      } catch (error) {
        console.error('Error fetching payment details:', error);
        if (error.response?.status === 404) {
          setErrorType('payment_not_found');
        } else {
          setErrorType('api_error');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchPaymentDetails();
  }, [referenceNo, error]);
  
  // Get response message based on code
  const getResponseMessage = (code) => {
    const messages = {
      'E001': 'Unauthorized Payment Mode',
      'E002': 'Unauthorized Key',
      'E003': 'Unauthorized Packet',
      'E004': 'Unauthorized Merchant',
      'E005': 'Unauthorized Return URL',
      'E006': 'Transaction Already Paid',
      'E007': 'Transaction Failed',
      'E008': 'Failure from Third Party due to Technical Error',
      'E009': 'Bill Already Expired',
      'E0031': 'Mandatory fields coming from merchant are empty',
      'E0032': 'Mandatory fields coming from database are empty',
      'E0033': 'Payment mode coming from merchant is empty',
      'E0034': 'PG Reference number coming from merchant is empty',
      'E0035': 'Sub merchant id coming from merchant is empty',
      'E0036': 'Transaction amount coming from merchant is empty',
      'E0037': 'Payment mode coming from merchant is other than 0 to 9',
      'E0038': 'Transaction amount coming from merchant is more than 9 digit length',
      'E0039': 'Mandatory value Email in wrong format',
      'E00333': 'Referer value is null / invalid Referer'
    };
    
    return messages[code] || 'Payment processing failed';
  };
  
  // Get error message based on error type
  const getErrorMessage = () => {
    if (responseCode) {
      return getResponseMessage(responseCode);
    }
    
    const errorMessages = {
      'payment_not_found': 'We could not find your payment record. Please try again.',
      'processing_error': 'There was an error processing your payment. Please try again.',
      'api_error': 'There was an error retrieving your payment details. Please try again.',
      'user_not_found': 'We could not identify the user associated with this payment. Please try again.',
      'unknown': 'Your payment could not be processed. Please try again.'
    };
    
    return errorMessages[errorType] || 'Your payment could not be processed. Please try again.';
  };
  
  const handleTryAgain = () => {
    navigate(`/book-slot/${programId}`); // Navigate back to booking page
  };
  
  if (loading) {
    return (
      <PaymentFailedContainer>
        <Header />
        <Content>
          <FailureCard>
            <Message>Loading payment details...</Message>
          </FailureCard>
        </Content>
      </PaymentFailedContainer>
    );
  }
  
  return (
    <PaymentFailedContainer>
      <Header />
      <Content>
        <FailureCard>
          <IconContainer>
            <FailureIcon>✕</FailureIcon>
          </IconContainer>
          
          <Title>Payment Failed</Title>
          
          {responseCode && (
            <ResponseCode>
              Response Code: {responseCode} - {getResponseMessage(responseCode)}
            </ResponseCode>
          )}
          
          <Message>{getErrorMessage()}</Message>
          
          {paymentDetails && (
            <div>
              <DetailItem>
                <DetailLabel>Reference Number:</DetailLabel>
                <DetailValue>{paymentDetails.reference_no}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Amount:</DetailLabel>
                <DetailValue>₹ {paymentDetails.amount}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Status:</DetailLabel>
                <DetailValue>{paymentDetails.status === 'failed' ? 'Failed' : 'Pending'}</DetailValue>
              </DetailItem>
              {paymentDetails.response_message && (
                <DetailItem>
                  <DetailLabel>Message:</DetailLabel>
                  <DetailValue>{paymentDetails.response_message}</DetailValue>
                </DetailItem>
              )}
              {paymentDetails.response_code && (
                <DetailItem>
                  <DetailLabel>Response Code:</DetailLabel>
                  <DetailValue>{paymentDetails.response_code}</DetailValue>
                </DetailItem>
              )}
            </div>
          )}
          
          <ActionButton 
            size="large" 
            onClick={handleTryAgain}
            fullWidth
          >
            Try Again
          </ActionButton>
        </FailureCard>
      </Content>
    </PaymentFailedContainer>
  );
};

export default PaymentFailed;
